import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { faPencil, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Formatter as CategoryFormatter } from '@admin/components/categories/formatter';
import { Labels as DimensionsLabels } from '@admin/components/dimensions/labels';
import { SingleSelectFormControl } from '@admin/components/fields/single_select_form_control';
import { client } from '@admin/libraries/apollo';
import { useItemCategoriesQuery, useItemUpdateMutation, Status } from '@admin/schema';
import { IItem } from '@admin/types/item';
import { normalizeItemToGraphQL, createUpdatedItemWithBothFormats } from '@admin/utils/item_normalization';

// Helper function to check if a category allows custom names
const isCustomNameCategory = (category?: { custom?: boolean }): boolean => {
  return category?.custom === true;
};

interface IOption {
  value: string;
  label: string;
}

interface ItemCategoryEditorProps {
  item: IItem;
  onSave?: (updatedItem: IItem) => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

export const ItemCategoryEditor: React.FC<ItemCategoryEditorProps> = ({
  item: rawItem,
  onSave,
  onCancel,
  readOnly = false,
}) => {
  // Normalize Angular fields to GraphQL format (camelCase)
  const [item, setItem] = useState(() => normalizeItemToGraphQL(rawItem));

  // Update normalized item when prop changes
  useEffect(() => {
    setItem(normalizeItemToGraphQL(rawItem));
  }, [rawItem]);

  const [editingMode, setEditingMode] = useState(false);
  const [editDimensionsMode, setEditDimensionsMode] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Form state using normalized GraphQL format
  const [categoryID, setCategoryID] = useState<string | undefined>(item.category?.id?.toString());
  const [sizingSelection, setSizingSelection] = useState<string | undefined>(item.sizingSelection);
  const [customCategoryName, setCustomCategoryName] = useState<string | undefined>(item.customCategoryName);
  const [length, setLength] = useState<number | undefined>(item.length);
  const [width, setWidth] = useState<number | undefined>(item.width);
  const [height, setHeight] = useState<number | undefined>(item.height);
  const [numMovers, setNumMovers] = useState<number | undefined>(item.numMovers);
  const [dimensionsOverwritten, setDimensionsOverwritten] = useState<boolean>(item.dimensionsOverwritten || false);

  // GraphQL queries and mutations
  const { data: itemCategories, loading: loadingCategories } = useItemCategoriesQuery({
    client,
  });

  const [executeItemUpdate, { loading: saving }] = useItemUpdateMutation({
    client,
  });

  // Get available categories with custom categories always at the end
  const categories = useMemo(() => {
    if (!itemCategories?.categories) return [];

    const allCategories = itemCategories.categories;

    // Separate regular and custom categories
    const regularCategories = allCategories.filter(
      (category) =>
        !isCustomNameCategory(category) &&
        ((category.moveAndPackVisible && !category.deletedAt) ||
          (item.category && category.id === item.category.id?.toString())),
    );

    const customCategories = allCategories.filter((category) => isCustomNameCategory(category));

    // Always put custom categories at the end
    return [...regularCategories, ...customCategories];
  }, [itemCategories?.categories, item.category]);

  // Get selected category and its sizing metric
  const selectedCategory = categories.find((cat) => cat.id === categoryID);
  const sizingMetric = selectedCategory?.sizingMetric;

  // Reset form when item changes
  const resetForm = useCallback(() => {
    setCategoryID(item.category?.id?.toString());
    setSizingSelection(item.sizingSelection);
    setCustomCategoryName(item.customCategoryName);
    setLength(item.length);
    setWidth(item.width);
    setHeight(item.height);
    setNumMovers(item.numMovers);
    setDimensionsOverwritten(item.dimensionsOverwritten || false);
  }, [item]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  // Set category to custom category if item has custom category name but no category set
  useEffect(() => {
    if (categories.length > 0 && item.customCategoryName && !item.category?.id) {
      const customNameCategory = categories.find((cat) => isCustomNameCategory(cat));
      if (customNameCategory) {
        setCategoryID(customNameCategory.id);
      }
    }
  }, [categories, item.customCategoryName, item.category?.id]);

  const handleCategoryChange = useCallback(
    (newCategoryID: string | undefined) => {
      setCategoryID(newCategoryID);
      setSizingSelection(undefined); // Reset sizing when category changes

      const newCategory = categories.find((cat) => cat.id === newCategoryID);

      // Always clear custom category name when switching to a non-custom category
      if (!isCustomNameCategory(newCategory)) {
        setCustomCategoryName(undefined);
      }

      // Update dimensions to new category defaults if not in edit dimensions mode
      if (!editDimensionsMode && newCategory?.categoryDefaults?.defaults) {
        const defaults = newCategory.categoryDefaults.defaults[0];
        if (defaults) {
          setLength(Math.round(defaults.length));
          setWidth(Math.round(defaults.width));
          setHeight(Math.round(defaults.height));
          setNumMovers(defaults.numMovers ?? undefined);
          setDimensionsOverwritten(false);
        }
      } else if (!editDimensionsMode && !newCategory?.categoryDefaults?.defaults) {
        setLength(undefined);
        setWidth(undefined);
        setHeight(undefined);
        setNumMovers(undefined);
        setDimensionsOverwritten(false);
      }
    },
    [categories, editDimensionsMode],
  );

  const handleSizingSelectionChange = useCallback(
    (newSizingSelection: string | undefined) => {
      setSizingSelection(newSizingSelection);

      if (
        !editDimensionsMode &&
        !dimensionsOverwritten &&
        newSizingSelection &&
        selectedCategory?.categoryDefaults?.defaults
      ) {
        const defaults =
          selectedCategory.categoryDefaults.defaults.find((d) => d.sizingSelection === newSizingSelection) ||
          selectedCategory.categoryDefaults.defaults[0];

        if (defaults) {
          setLength(Math.round(defaults.length));
          setWidth(Math.round(defaults.width));
          setHeight(Math.round(defaults.height));
          setNumMovers(defaults.numMovers ?? undefined);
        }
      }
    },
    [editDimensionsMode, dimensionsOverwritten, selectedCategory],
  );

  const handleEditDimensions = useCallback(() => {
    setEditDimensionsMode(true);
    setDimensionsOverwritten(true);
  }, []);

  const handleRestoreDimensions = useCallback(() => {
    setEditDimensionsMode(false);
    setDimensionsOverwritten(false);

    // Reset to default dimensions if available
    const categoryDefaults = selectedCategory?.categoryDefaults;
    if (categoryDefaults?.defaults) {
      const defaults =
        categoryDefaults.defaults.find((d) => d.sizingSelection === sizingSelection) || categoryDefaults.defaults[0];

      if (defaults) {
        setLength(Math.round(defaults.length));
        setWidth(Math.round(defaults.width));
        setHeight(Math.round(defaults.height));
        setNumMovers(defaults.numMovers ?? undefined);
      }
    } else {
      // Clear dimensions if no defaults available
      setLength(undefined);
      setWidth(undefined);
      setHeight(undefined);
      setNumMovers(undefined);
    }
  }, [selectedCategory, sizingSelection]);

  const handleSave = async () => {
    if (!dimensionsPresent && editDimensionsMode) {
      setError('Please enter all dimensions (length, width, height)');
      return;
    }

    // Require custom category name when custom category is selected
    if (isCustomNameCategory(selectedCategory) && (!customCategoryName || customCategoryName.trim() === '')) {
      setError('Please enter a custom category name');
      return;
    }

    try {
      // If custom category name is provided, ensure we're using a custom name category
      let finalCategoryID = categoryID;
      if (customCategoryName) {
        const customNameCategory = categories.find((cat) => isCustomNameCategory(cat));
        if (customNameCategory) {
          finalCategoryID = customNameCategory.id;
        }
      }

      const result = await executeItemUpdate({
        variables: {
          input: {
            itemID: item.id.toString(),
            categoryID: finalCategoryID,
            sizingSelection,
            customCategoryName: customCategoryName || null,
            length,
            width,
            height,
            numMovers,
            dimensionsOverwritten,
          },
        },
      });

      const resultFields = result.data?.itemUpdate;

      if (resultFields?.status !== Status.Ok) {
        setError(resultFields?.error || 'Unable to update item');
      } else if (resultFields?.item) {
        setError(undefined);
        setEditingMode(false);
        setEditDimensionsMode(false);

        // Call onSave callback with updated item data in both formats
        if (onSave) {
          const updatedItem = createUpdatedItemWithBothFormats(rawItem, {
            category: resultFields.item.category || undefined,
            sizingSelection: resultFields.item.sizingSelection || undefined,
            customCategoryName: resultFields.item.customCategoryName ?? undefined,
            length: resultFields.item.length ?? undefined,
            width: resultFields.item.width ?? undefined,
            height: resultFields.item.height ?? undefined,
            numMovers: resultFields.item.numMovers ?? undefined,
            dimensionsOverwritten: resultFields.item.dimensionsOverwritten || false,
            sizing_selection: resultFields.item.sizingSelection || undefined,
            custom_category_name: resultFields.item.customCategoryName ?? undefined,
            dimensions_overwritten: resultFields.item.dimensionsOverwritten || false,
            num_movers: resultFields.item.numMovers ?? undefined,
          });
          onSave(updatedItem);
        }
      }
    } catch (e) {
      setError('Unable to update item. Please try again.');
    }
  };

  const handleCancel = useCallback(() => {
    resetForm();
    setEditingMode(false);
    setEditDimensionsMode(false);
    setError(undefined);

    if (onCancel) {
      onCancel();
    }
  }, [resetForm, onCancel]);

  const dimensionsPresent = useMemo(() => {
    return (
      length !== null &&
      length !== undefined &&
      width !== null &&
      width !== undefined &&
      height !== null &&
      height !== undefined
    );
  }, [length, width, height]);

  const pluralize = useCallback((unit: string) => {
    switch (unit) {
      case 'shelf':
        return 'shelves';
      case 'cube':
        return 'cubes';
      case 'drawer':
        return 'drawers';
      case 'piece':
        return 'pieces';
      case 'inch':
        return 'inches';
      default:
        return unit;
    }
  }, []);

  if (loadingCategories) {
    return <div>Loading categories...</div>;
  }

  return (
    <div className="category-editor">
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {editingMode ? (
        <div>
          {/* Category Selection */}
          <div className="form-group">
            <SingleSelectFormControl
              options={categories.map((category): IOption => ({
                value: category.id,
                label: category.name,
              }))}
              onChange={handleCategoryChange}
              value={categoryID}
              placeholder="Select category..."
              filterOption={(option: any, inputValue: string) => {
                // Always show custom categories regardless of search input
                // There is currently only one on production: "Other Item".
                const category = categories.find((cat) => cat.id === option.value);
                if (isCustomNameCategory(category)) {
                  return true;
                }

                // For non-custom categories, use default filtering
                if (!inputValue) {
                  return true; // Show all when no search input
                }

                // Case-insensitive search on category name
                return option.label.toLowerCase().includes(inputValue.toLowerCase());
              }}
            />
          </div>

          {/* Sizing Selection */}
          {sizingMetric && sizingMetric.enumerations && sizingMetric.enumerations.length > 0 && (
            <div className="form-group">
              <SingleSelectFormControl
                options={sizingMetric.enumerations.map((selection): IOption => ({
                  value: selection,
                  label: sizingMetric.unit ? `${selection} ${pluralize(sizingMetric.unit)}` : selection,
                }))}
                onChange={handleSizingSelectionChange}
                value={sizingSelection}
                placeholder="Select sizing..."
              />
            </div>
          )}

          {/* Custom Category Name - show when custom category is selected */}
          {isCustomNameCategory(selectedCategory) && (
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                value={customCategoryName || ''}
                onChange={(e) => setCustomCategoryName(e.target.value)}
                placeholder="Custom category name (required)"
                disabled={saving}
                required
              />
            </div>
          )}

          {/* Dimensions */}
          <div className="input-dimensions-container">
            <div className="row">
              <div className="col-xs-11">
                <input
                  name="length"
                  type="number"
                  readOnly={!editDimensionsMode}
                  className="input-dimension col-xs-4"
                  value={length || ''}
                  onChange={(e) => setLength(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="L(in)"
                />
                <input
                  name="width"
                  type="number"
                  readOnly={!editDimensionsMode}
                  className="input-dimension col-xs-4"
                  value={width || ''}
                  onChange={(e) => setWidth(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="W(in)"
                />
                <input
                  name="height"
                  type="number"
                  readOnly={!editDimensionsMode}
                  className="input-dimension col-xs-4"
                  value={height || ''}
                  onChange={(e) => setHeight(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="H(in)"
                />
              </div>
              <div className="col-xs-1 edit-dimension-column">
                {!editDimensionsMode ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditDimensions();
                    }}
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </a>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRestoreDimensions();
                    }}
                  >
                    <FontAwesomeIcon icon={faRefresh} />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="buttons-control pull-right">
            <button className="btn btn-sm btn-warning" onClick={handleCancel} disabled={saving}>
              cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={
                saving ||
                (!dimensionsPresent && editDimensionsMode) ||
                (isCustomNameCategory(selectedCategory) && (!customCategoryName || customCategoryName.trim() === ''))
              }
            >
              {saving ? 'saving...' : 'save'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <span className="label label-default">
            <CategoryFormatter resource={item} fallback="No Category" />
          </span>
          <br />
          <DimensionsLabels resource={item} />
          {!readOnly && (
            <span className="show-for-role-admin show-for-role-warehouse">
              <a
                className="text-primary edit-button pull-right"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setEditingMode(true);
                }}
              >
                edit
              </a>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
